import { useState, useEffect } from 'react';
import { SlateByEventDto } from '@sportspredict/shared';

export const useTriggerNewMarketsPopup = (slates: SlateByEventDto[] | undefined) => {
	const [showNewMarkets, setShowNewMarkets] = useState<boolean>(false);
	const [activeSlate, setActiveSlate] = useState<SlateByEventDto | null>(null);

	useEffect(() => {
		if (!slates || !Array.isArray(slates)) return undefined;

		const timers: ReturnType<typeof setTimeout>[] = [];
		const now = Date.now();

		// Filter for slates that have a valid opening date and haven't been seen yet
		const unseenSlates = slates.filter((slate) => {
			if (!slate.opening_date || !slate.id) return false;
			const opening = new Date(slate.opening_date).getTime();
			if (isNaN(opening)) return false;

			const seenKey = `seen_new_markets_${slate.id}`;
			const hasSeen = localStorage.getItem(seenKey);
			return hasSeen !== 'true';
		});

		// Split into "missed" (already open) and "future" (scheduled)
		const missedSlates = unseenSlates.filter((slate) => {
			const opening = new Date(slate.opening_date!).getTime();
			return now >= opening;
		});

		const futureSlates = unseenSlates.filter((slate) => {
			const opening = new Date(slate.opening_date!).getTime();
			return now < opening;
		});

		// 1. Handle Missed Slates: Show ONLY the most recent one
		if (missedSlates.length > 0) {
			// Sort by opening_date DESC to find the "latest" one
			const sortedMissed = [...missedSlates].sort((a, b) => {
				const timeA = new Date(a.opening_date!).getTime();
				const timeB = new Date(b.opening_date!).getTime();
				return timeB - timeA;
			});

			const latestMissed = sortedMissed[0];
			setActiveSlate(latestMissed);
			setShowNewMarkets(true);

			// Mark ALL missed slates as seen immediately
			missedSlates.forEach((slate) => {
				localStorage.setItem(`seen_new_markets_${slate.id}`, 'true');
			});
		}

		// 2. Handle Future Slates: Set timers normally
		futureSlates.forEach((slate) => {
			const opening = new Date(slate.opening_date!).getTime();
			const delay = opening - now;

			const timer = setTimeout(() => {
				setActiveSlate(slate);
				setShowNewMarkets(true);
				localStorage.setItem(`seen_new_markets_${slate.id}`, 'true');
			}, delay);

			timers.push(timer);
		});

		return () => {
			timers.forEach((t) => clearTimeout(t));
		};
	}, [slates]);

	return {
		showNewMarkets,
		setShowNewMarkets,
		activeSlate,
	};
};
